import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { useCompany } from './CompanyContext';
import { useImpersonation } from './ImpersonationContext';
import { SGA_CLASSES } from './Products';
import { getErgGuideNumber, getErgGuide, getUniqueErgGuides } from './erg2024Data';
import { nivelProductos } from './lib/compatibilidad';
import { ubicacionesHabilitadas } from './lib/ubicaciones-flag';
import { fetchUbicacionesDeProductos, UbicacionDescrita } from './lib/ubicaciones';
import UbicacionChips from './components/ubicaciones/UbicacionChips';
import jsPDF from 'jspdf';
import logoSrc from './assets/storsafe-logo.png';
import './Emergency.css';
import SelectBuscable from './components/SelectBuscable';
import { hoyEc } from './lib/fechas';

// Bodega con buscador (PE-C1): mismo aspecto que tenía .emg-select.
const EMG_INPUT: React.CSSProperties = {
  background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0', padding: '8px 36px 8px 12px', borderRadius: 8, fontSize: '0.875rem',
};

// ── Types ─────────────────────────────────────────────────────────────────────

interface Product {
  id: string;
  name: string;
  cas_number: string | null;
  un_number: string | null;
  sga_classes: string[];
}

interface Warehouse {
  id: string;
  name: string;
  description: string | null;
}

interface Zone {
  id: string;
  warehouse_id: string;
  name: string;
  product_ids: string[];
}

// ── Compatibilidad: tabla única (src/lib/compatibilidad.ts) ─────────────────
// Aquí el nivel 'precaucion' se sigue llamando 'segregar' internamente.

function productCompat(a: Product, b: Product): 'incompatible' | 'segregar' | 'compatible' {
  const nivel = nivelProductos(a, b);
  return nivel === 'precaucion' ? 'segregar' : nivel;
}


// Las líneas 101, 102, 103, 112, 131 y 149 fueron inhabilitadas por ECU 911 desde
// el 17/03/2016: el único número de emergencias es el 911.
// CIATOX 1800 836366 (1800 VENENO, gratuita): fuente gacetas de efectos tóxicos
// del MSP (salud.gob.ec, 2021); verificado el 13/10/2026.
const EMERGENCY_NUMBERS = [
  { name: 'ECU-911 — coordina bomberos, policía y ambulancia', number: '911',           color: '#ef4444' as const, isMain: true  },
  { name: 'CIATOX — Centro de Información y Asesoramiento Toxicológico (MSP)', number: '1800 836366', color: '#22c55e' as const, isMain: false },
];

// ── Utils ─────────────────────────────────────────────────────────────────────

function truncate(s: string, n: number) { return s.length > n ? s.slice(0, n - 1) + '...' : s; }
function stripAccents(s: string): string { return s.normalize('NFD').replace(/[̀-ͯ]/g, ''); }
function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0,2),16), parseInt(h.slice(2,4),16), parseInt(h.slice(4,6),16)];
}

function getZoneRisk(zone: Zone, productMap: Map<string, Product>): 'ok' | 'segregar' | 'incompatible' {
  const prods = zone.product_ids.map(id => productMap.get(id)).filter(Boolean) as Product[];
  let worst: 'ok' | 'segregar' | 'incompatible' = 'ok';
  for (let i = 0; i < prods.length; i++) {
    for (let j = i + 1; j < prods.length; j++) {
      const r = productCompat(prods[i], prods[j]);
      if (r === 'incompatible') return 'incompatible';
      if (r === 'segregar') worst = 'segregar';
    }
  }
  return worst;
}

function getZonePairs(zone: Zone, productMap: Map<string, Product>) {
  const prods = zone.product_ids.map(id => productMap.get(id)).filter(Boolean) as Product[];
  const incomp: { a: Product; b: Product }[] = [];
  const segr:   { a: Product; b: Product }[] = [];
  for (let i = 0; i < prods.length; i++) {
    for (let j = i + 1; j < prods.length; j++) {
      const r = productCompat(prods[i], prods[j]);
      if (r === 'incompatible') incomp.push({ a: prods[i], b: prods[j] });
      else if (r === 'segregar') segr.push({ a: prods[i], b: prods[j] });
    }
  }
  return { incomp, segr };
}

// ── PDF Generation ────────────────────────────────────────────────────────────

interface PdfArgs {
  warehouse: Warehouse;
  zones: Zone[];
  productMap: Map<string, Product>;
  allProducts: Product[];
  companyName: string;
  logoImg: HTMLImageElement | null;
}

function generateEmergencyPdf({ warehouse, zones, productMap, allProducts, companyName, logoImg }: PdfArgs) {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pw  = pdf.internal.pageSize.getWidth();
  const ph  = pdf.internal.pageSize.getHeight();
  const M   = 14;
  const UW  = pw - 2 * M;
  const FTR = 18;
  let   y   = 0;

  const fill   = (r: number, g: number, b: number) => pdf.setFillColor(r, g, b);
  const color  = (r: number, g: number, b: number) => pdf.setTextColor(r, g, b);
  const stroke = (r: number, g: number, b: number) => pdf.setDrawColor(r, g, b);
  const font   = (style: 'normal'|'bold'|'italic', size: number) => { pdf.setFont('helvetica', style); pdf.setFontSize(size); };
  const rect   = (x: number, ry: number, w: number, h: number) => pdf.rect(x, ry, w, h, 'F');
  const rrect  = (x: number, ry: number, w: number, h: number, r: number) => pdf.roundedRect(x, ry, w, h, r, r, 'F');
  const txt    = (s: string, x: number, ty: number, opts?: object) => pdf.text(s, x, ty, opts ?? {});
  const line   = (x1: number, y1: number, x2: number, y2: number) => pdf.line(x1, y1, x2, y2);

  let pageNum = 1;

  const addFooter = () => {
    fill(15, 23, 42); rect(0, ph - FTR, pw, FTR);
    fill(239, 68, 68); rect(0, ph - FTR, pw, 0.5);
    font('italic', 5.5); color(71, 85, 105);
    txt('AVISO LEGAL: StorSafe es una herramienta de apoyo. La informacion es responsabilidad exclusiva del usuario.', M, ph - 12.5);
    txt('No reemplaza asesoria profesional en seguridad quimica. StorSafe no asume responsabilidad por danos directos, indirectos o lucro cesante.', M, ph - 8);
    font('normal', 6.5); color(100, 116, 139);
    txt('Plan de Emergencia Quimica - StorSafe', M, ph - 3.5);
    txt(`Pag. ${pageNum}`, pw - M, ph - 3.5, { align: 'right' });
  };

  const contHeader = (subtitle: string) => {
    fill(15, 23, 42); rect(0, 0, pw, 12);
    fill(239, 68, 68); rect(0, 11, pw, 1);
    font('bold', 7); color(239, 68, 68); txt('PLAN DE EMERGENCIA', M, 8);
    font('normal', 7); color(100, 116, 139);
    txt(`· ${truncate(warehouse.name, 30)} · ${companyName}`, M + 34, 8);
    font('italic', 7); color(71, 85, 105); txt(subtitle, pw - M, 8, { align: 'right' });
  };

  const checkPage = (needed: number, subtitle: string) => {
    if (y + needed > ph - FTR - 4) {
      addFooter();
      pageNum++;
      pdf.addPage();
      contHeader(subtitle);
      y = 18;
    }
  };

  const section = (num: string, title: string, r: number, g: number, b: number) => {
    checkPage(14, title);
    fill(22, 33, 51); rect(M, y, UW, 10);
    fill(r, g, b); rect(M, y, 4, 10);
    font('bold', 9); color(226, 232, 240);
    txt(`${num}  ${title}`, M + 8, y + 7);
    y += 15;
  };

  // ── Determine which classes are present ───────────────────────────────────

  const whProductIds = new Set(zones.flatMap(z => z.product_ids));
  const whProducts   = Array.from(whProductIds).map(id => productMap.get(id)).filter(Boolean) as Product[];
  const classesPresent = new Set(whProducts.flatMap(p => p.sga_classes));

  const allIncompat: { a: Product; b: Product; zone: string }[] = [];
  const allSegr:     { a: Product; b: Product; zone: string }[] = [];
  for (const zone of zones) {
    const { incomp, segr } = getZonePairs(zone, productMap);
    incomp.forEach(p => allIncompat.push({ ...p, zone: zone.name }));
    segr.forEach(p => allSegr.push({ ...p, zone: zone.name }));
  }

  const genDate = stripAccents(new Date().toLocaleDateString('es-EC', { weekday:'long', year:'numeric', month:'long', day:'numeric' }));

  // ── COVER PAGE ────────────────────────────────────────────────────────────

  fill(15, 23, 42); rect(0, 0, pw, ph);
  fill(239, 68, 68); rect(0, 0, pw, 2);
  fill(239, 68, 68); rect(0, ph - 2, pw, 2);

  // Warning stripe top
  fill(30, 15, 15); rect(0, 2, pw, 22);
  font('bold', 9); color(239, 68, 68);
  txt('DOCUMENTO DE USO INTERNO · MANTENER VISIBLE EN LA BODEGA', pw / 2, 15, { align: 'center' });
  font('normal', 7.5); color(150, 50, 50);
  txt('En caso de emergencia llamar al 911 (ECU-911)', pw / 2, 20.5, { align: 'center' });

  // Logo
  if (logoImg) {
    const lw = 65; const lh = lw * (logoImg.naturalHeight / logoImg.naturalWidth);
    pdf.addImage(logoImg, 'PNG', pw / 2 - lw / 2, 30, lw, lh);
  } else {
    fill(239, 68, 68); rrect(pw / 2 - 22, 34, 44, 44, 5);
    font('bold', 18); color(255, 255, 255); txt('!', pw / 2, 48, { align: 'center' });
    font('bold', 8); color(255, 255, 255); txt('StorSafe', pw / 2, 60, { align: 'center' });
  }

  // Title
  font('bold', 20); color(241, 245, 249);
  txt('PLAN DE', pw / 2, 92, { align: 'center' });
  txt('EMERGENCIA', pw / 2, 104, { align: 'center' });
  font('bold', 14); color(239, 68, 68);
  txt('QUIMICA', pw / 2, 115, { align: 'center' });

  fill(239, 68, 68); rect(pw / 2 - 35, 119, 70, 1);

  font('normal', 9); color(100, 116, 139);
  txt('Almacenamiento de Sustancias Quimicas Peligrosas', pw / 2, 127, { align: 'center' });

  // Info box
  fill(22, 33, 51); rrect(pw / 2 - 60, 138, 120, 62, 3);
  fill(239, 68, 68); rect(pw / 2 - 60, 138, 4, 62);

  font('normal', 7.5); color(100, 116, 139); txt('BODEGA', pw / 2 - 52, 150);
  font('bold', 12); color(241, 245, 249);
  const whLines = pdf.splitTextToSize(warehouse.name, 100);
  txt(whLines[0], pw / 2 - 52, 159);

  font('normal', 7.5); color(100, 116, 139); txt('EMPRESA', pw / 2 - 52, 170);
  font('bold', 10); color(226, 232, 240);
  const coLines = pdf.splitTextToSize(companyName, 100);
  txt(coLines[0], pw / 2 - 52, 179);

  font('normal', 7.5); color(100, 116, 139); txt('FECHA DE EMISION', pw / 2 - 52, 189);
  font('normal', 8); color(148, 163, 184); txt(genDate, pw / 2 - 52, 196);

  // Emergency numbers strip
  fill(30, 15, 15); rrect(M, 210, UW, 32, 3);
  fill(239, 68, 68); rect(M, 210, 4, 32);
  font('bold', 8); color(252, 165, 165); txt('NUMEROS DE EMERGENCIA ECUADOR', M + 8, 220);
  const numCols = 2;
  const numW = (UW - 8) / numCols;
  EMERGENCY_NUMBERS.forEach((n, i) => {
    const col = i % numCols;
    const row = Math.floor(i / numCols);
    const nx = M + 8 + col * numW;
    const ny = 226 + row * 10;
    const [nr, ng, nb] = hexToRgb(n.color);
    if (n.isMain) {
      font('bold', 11); color(nr, ng, nb); txt(n.number, nx, ny);
      font('normal', 6); color(148, 163, 184); txt(n.name, nx, ny + 5.5);
    } else {
      font('bold', 8); color(nr, ng, nb); txt(n.number, nx, ny);
      font('normal', 6.5); color(148, 163, 184); txt(n.name, nx, ny + 4.5);
    }
  });

  // Cover footer
  fill(20, 30, 20); rect(0, ph - 28, pw, 26);
  fill(239, 68, 68); rect(0, ph - 28, pw, 0.5);
  font('bold', 9); color(239, 68, 68); txt('StorSafe', pw / 2, ph - 18, { align: 'center' });
  font('normal', 7); color(100, 116, 139);
  txt('Sistema de Gestion de Bodegas Quimicas', pw / 2, ph - 11, { align: 'center' });
  txt(`Generado automaticamente el ${new Date().toLocaleDateString('es-EC')} · Documento sujeto a actualizacion`, pw / 2, ph - 5, { align: 'center' });

  // ── PAGE 2: WAREHOUSE INFO + INVENTORY ───────────────────────────────────

  pdf.addPage();
  pageNum = 2;
  contHeader('Informacion de la Bodega');
  y = 18;

  // SECTION 1 — Warehouse info
  section('1.', 'INFORMACION DE LA BODEGA', 59, 130, 246);

  const infoRows = [
    ['Nombre de la bodega', warehouse.name],
    ['Empresa',             companyName],
    ['Descripcion',         warehouse.description ?? 'No especificada'],
    ['Total de zonas',      String(zones.length)],
    ['Total de productos',  String(whProducts.length)],
    ['Clases SGA presentes',Array.from(classesPresent).join(', ') || 'Ninguna'],
  ];

  for (const [label, value] of infoRows) {
    checkPage(9, 'Informacion de la Bodega');
    fill(22, 33, 51); rect(M, y, UW, 8);
    fill(59, 130, 246); rect(M, y, 3.5, 8);
    font('bold', 7.5); color(100, 116, 139); txt(label, M + 7, y + 5.5);
    font('normal', 7.5); color(203, 213, 225); txt(truncate(value, 80), M + 65, y + 5.5);
    y += 8;
    fill(15, 23, 42); rect(M, y, UW, 0.5);
    y += 1;
  }
  y += 6;

  // Zone list
  checkPage(16, 'Informacion de la Bodega');
  fill(22, 33, 51); rect(M, y, UW, 8);
  fill(168, 85, 247); rect(M, y, 3.5, 8);
  font('bold', 8); color(226, 232, 240); txt('Zonas registradas', M + 7, y + 5.5);
  y += 8;

  if (zones.length === 0) {
    checkPage(8, 'Informacion de la Bodega');
    font('italic', 7.5); color(71, 85, 105); txt('Sin zonas registradas.', M + 7, y + 5.5);
    y += 8;
  } else {
    for (const zone of zones) {
      checkPage(8, 'Informacion de la Bodega');
      const risk = getZoneRisk(zone, productMap);
      fill(17, 24, 39); rect(M, y, UW, 7.5);
      const dotColor: [number,number,number] = risk === 'incompatible' ? [239,68,68] : risk === 'segregar' ? [245,158,11] : [34,197,94];
      fill(...dotColor); rect(M + 4, y + 2, 2, 3.5);
      font('normal', 7.5); color(203, 213, 225); txt(zone.name, M + 10, y + 5.2);
      font('normal', 7); color(100, 116, 139);
      txt(`${zone.product_ids.length} prod.`, M + 90, y + 5.2);
      font('bold', 7); color(...dotColor);
      const riskLabel = risk === 'incompatible' ? 'ALERTA' : risk === 'segregar' ? 'PRECAUCION' : 'OK';
      txt(riskLabel, pw - M - 4, y + 5.2, { align: 'right' });
      y += 7.5;
    }
  }
  y += 8;

  // SECTION 2 — Hazardous substances inventory
  section('2.', 'INVENTARIO DE SUSTANCIAS PELIGROSAS', 245, 158, 11);

  if (whProducts.length === 0) {
    checkPage(10, 'Inventario');
    font('italic', 7.5); color(71, 85, 105); txt('No hay productos asignados a zonas en esta bodega.', M + 4, y + 5);
    y += 10;
  } else {
    // Table header
    const tableHeader2 = () => {
      fill(22, 33, 51); rect(M, y, UW, 7.5);
      font('bold', 7); color(100, 116, 139);
      txt('#',            M + 2,    y + 5.5);
      txt('Producto',     M + 8,    y + 5.5);
      txt('N CAS',        M + 100,  y + 5.5);
      txt('Clases SGA',   M + 135,  y + 5.5);
      y += 7.5;
    };
    tableHeader2();

    whProducts.forEach((p, idx) => {
      checkPage(7, 'Inventario');
      fill(idx % 2 === 0 ? 15 : 18, idx % 2 === 0 ? 23 : 26, idx % 2 === 0 ? 42 : 46);
      rect(M, y, UW, 7);
      font('normal', 7); color(203, 213, 225);
      txt(String(idx + 1),              M + 2,   y + 5);
      txt(truncate(p.name, 42),         M + 8,   y + 5);
      txt(p.cas_number ?? '--',         M + 100, y + 5);
      txt(p.sga_classes.join(', ') || '--', M + 135, y + 5);
      y += 7;
    });
  }
  y += 8;

  // SECTION 3 — Identified risks
  section('3.', 'RIESGOS IDENTIFICADOS POR ZONA', 239, 68, 68);

  const hasRisks = allIncompat.length > 0 || allSegr.length > 0;

  if (!hasRisks) {
    checkPage(14, 'Riesgos');
    fill(15, 30, 20); rect(M, y, UW, 12);
    fill(34, 197, 94); rect(M, y, 3.5, 12);
    font('bold', 8.5); color(134, 239, 172);
    txt('No se detectaron incompatibilidades.', M + 7, y + 8);
    y += 16;
  } else {
    if (allIncompat.length > 0) {
      checkPage(12, 'Riesgos');
      fill(40, 20, 20); rect(M, y, UW, 8);
      fill(239, 68, 68); rect(M, y, 3.5, 8);
      font('bold', 8); color(252, 165, 165);
      txt(`INCOMPATIBLES - NO deben almacenarse juntos (${allIncompat.length})`, M + 7, y + 5.5);
      y += 8;

      allIncompat.forEach((pair, idx) => {
        checkPage(8, 'Riesgos');
        fill(idx % 2 === 0 ? 35 : 30, idx % 2 === 0 ? 15 : 12, idx % 2 === 0 ? 15 : 12);
        rect(M, y, UW, 8);
        fill(239, 68, 68); rrect(M + 2, y + 2, 3, 4, 0.5);
        font('bold', 7); color(252, 165, 165); txt(truncate(pair.a.name, 34), M + 8, y + 5.5);
        font('normal', 7); color(150, 50, 50); txt('NO COMPATIBLE', M + 90, y + 5.5);
        font('bold', 7); color(252, 165, 165); txt(truncate(pair.b.name, 28), M + 118, y + 5.5);
        font('italic', 6.5); color(100, 116, 139); txt(`Zona: ${pair.zone}`, pw - M - 2, y + 5.5, { align: 'right' });
        y += 8;
      });
      y += 4;
    }

    if (allSegr.length > 0) {
      checkPage(12, 'Riesgos');
      fill(35, 28, 10); rect(M, y, UW, 8);
      fill(245, 158, 11); rect(M, y, 3.5, 8);
      font('bold', 8); color(253, 230, 138);
      txt(`PRECAUCION - Separar fisicamente segun la ficha de datos de seguridad (SDS) (${allSegr.length})`, M + 7, y + 5.5);
      y += 8;

      allSegr.forEach((pair, idx) => {
        checkPage(8, 'Riesgos');
        fill(idx % 2 === 0 ? 30 : 25, idx % 2 === 0 ? 22 : 18, idx % 2 === 0 ? 10 : 8);
        rect(M, y, UW, 8);
        fill(245, 158, 11); rrect(M + 2, y + 2, 3, 4, 0.5);
        font('bold', 7); color(253, 230, 138); txt(truncate(pair.a.name, 34), M + 8, y + 5.5);
        font('normal', 7); color(150, 120, 30); txt('SEPARAR (VER SDS)', M + 90, y + 5.5);
        font('bold', 7); color(253, 230, 138); txt(truncate(pair.b.name, 28), M + 118, y + 5.5);
        font('italic', 6.5); color(100, 116, 139); txt(`Zona: ${pair.zone}`, pw - M - 2, y + 5.5, { align: 'right' });
        y += 8;
      });
      y += 4;
    }
  }

  // SECTION 4 — ERG 2024
  section('4.', 'GUIA DE RESPUESTA A EMERGENCIAS ERG 2024', 16, 185, 129);

  const ergGuides = getUniqueErgGuides(whProducts.map(p => p.un_number));

  if (ergGuides.length === 0) {
    checkPage(10, 'ERG 2024');
    font('italic', 7.5); color(71, 85, 105);
    txt('No se encontraron guias ERG para los productos de esta bodega (sin numero UN/NA registrado).', M + 4, y + 5);
    y += 10;
  } else {
    // Products with UN linked to each guide
    for (const guide of ergGuides) {
      const linkedProducts = whProducts.filter(p => getErgGuideNumber(p.un_number) === guide.number);

      checkPage(18, `ERG ${guide.number}`);
      fill(20, 40, 35); rect(M, y, UW, 10);
      fill(16, 185, 129); rect(M, y, 4, 10);
      font('bold', 9); color(110, 231, 183);
      txt(`GUIA ERG ${guide.number} — ${guide.name}`, M + 8, y + 7);
      y += 10;

      // Products linked
      checkPage(8, `ERG ${guide.number}`);
      fill(15, 30, 25); rect(M, y, UW, 7);
      font('bold', 6.5); color(71, 85, 105); txt('Productos:', M + 4, y + 5);
      font('normal', 6.5); color(148, 163, 184);
      txt(linkedProducts.map(p => `${p.name}${p.un_number ? ` (UN${p.un_number})` : ''}`).join(', '), M + 28, y + 5);
      y += 7;

      // Hazards
      checkPage(20, `ERG ${guide.number}`);
      fill(35, 20, 20); rect(M, y, UW / 2 - 1, 6);
      fill(239, 68, 68); rect(M, y, 3, 6);
      font('bold', 7); color(252, 165, 165); txt('INCENDIO / EXPLOSION', M + 6, y + 4.5);
      y += 6;
      const fireLines = pdf.splitTextToSize(guide.hazards.fireExplosion, UW / 2 - 8);
      const fireH = fireLines.length * 5.5 + 2;
      checkPage(fireH + 2, `ERG ${guide.number}`);
      fill(25, 15, 15); rect(M, y, UW / 2 - 1, fireH);
      font('normal', 6); color(203, 213, 225);
      for (let i = 0; i < fireLines.length; i++) txt(fireLines[i], M + 4, y + 4.5 + i * 5.5);
      const saveY = y;
      fill(20, 20, 35); rect(M + UW / 2 + 1, saveY, UW / 2 - 1, 6);
      fill(245, 158, 11); rect(M + UW / 2 + 1, saveY, 3, 6);
      font('bold', 7); color(253, 230, 138); txt('SALUD', M + UW / 2 + 7, saveY + 4.5);
      const healthLines = pdf.splitTextToSize(guide.hazards.health, UW / 2 - 8);
      const healthH = healthLines.length * 5.5 + 2;
      const blockH = Math.max(fireH, healthH);
      fill(15, 15, 25); rect(M + UW / 2 + 1, saveY + 6, UW / 2 - 1, blockH);
      font('normal', 6); color(203, 213, 225);
      for (let i = 0; i < healthLines.length; i++) txt(healthLines[i], M + UW / 2 + 5, saveY + 6 + 4.5 + i * 5.5);

      y = saveY + 6 + blockH + 2;

      // Public safety distances
      checkPage(22, `ERG ${guide.number}`);
      fill(22, 33, 51); rect(M, y, UW, 6);
      fill(59, 130, 246); rect(M, y, 3, 6);
      font('bold', 7); color(147, 197, 253); txt('SEGURIDAD PUBLICA — DISTANCIAS DE AISLAMIENTO', M + 6, y + 4.5);
      y += 6;

      const safetyRows = [
        ['Derrame pequeno:', guide.publicSafety.isolationSmallSpill],
        ['Derrame grande:', guide.publicSafety.isolationLargeSpill],
        ['Incendio tanques:', guide.publicSafety.fireIsolation],
        ['Accion protectora pequeno:', guide.publicSafety.protectiveActionSmall],
        ['Accion protectora grande:', guide.publicSafety.protectiveActionLarge],
      ];
      for (const [lbl, val] of safetyRows) {
        checkPage(7, `ERG ${guide.number}`);
        fill(17, 24, 39); rect(M, y, UW, 6.5);
        font('bold', 6.5); color(100, 116, 139); txt(lbl, M + 4, y + 4.5);
        font('normal', 6.5); color(203, 213, 225);
        const valLines = pdf.splitTextToSize(val, UW - 65);
        txt(valLines[0], M + 62, y + 4.5);
        y += 6.5;
      }
      y += 2;

      // Fire by type
      checkPage(14, `ERG ${guide.number}`);
      fill(22, 33, 51); rect(M, y, UW, 6);
      fill(239, 68, 68); rect(M, y, 3, 6);
      font('bold', 7); color(252, 165, 165); txt('RESPUESTA — INCENDIO POR TIPO', M + 6, y + 4.5);
      y += 6;

      const fireTypeRows: [string, string][] = [
        ['Incendio pequeno:', guide.fireByType.small],
        ['Incendio grande:', guide.fireByType.large],
        ['Tanques/contenedores:', guide.fireByType.tank],
      ];
      for (const [lbl, val] of fireTypeRows) {
        checkPage(7, `ERG ${guide.number}`);
        fill(28, 15, 15); rect(M, y, UW, 6.5);
        fill(239, 68, 68); rect(M, y, 2, 6.5);
        font('bold', 6.5); color(252, 165, 165); txt(lbl, M + 5, y + 4.5);
        font('normal', 6.5); color(203, 213, 225);
        const valLines = pdf.splitTextToSize(val, UW - 55);
        txt(valLines[0], M + 52, y + 4.5);
        y += 6.5;
      }
      y += 2;

      // PPE for responder
      checkPage(14, `ERG ${guide.number}`);
      fill(22, 33, 51); rect(M, y, UW, 6);
      fill(34, 197, 94); rect(M, y, 3, 6);
      font('bold', 7); color(134, 239, 172); txt('EPP DEL RESPONDEDOR DE EMERGENCIA', M + 6, y + 4.5);
      y += 6;

      const ppeRows: [string, string][] = [
        ['Traje:', guide.ppe.suit],
        ['Respirador:', guide.ppe.respirator],
        ['Guantes:', guide.ppe.gloves],
        ['Botas:', guide.ppe.boots],
      ];
      for (let idx = 0; idx < ppeRows.length; idx++) {
        const [lbl, val] = ppeRows[idx];
        checkPage(7, `ERG ${guide.number}`);
        fill(idx % 2 === 0 ? 15 : 18, idx % 2 === 0 ? 28 : 24, idx % 2 === 0 ? 20 : 18);
        rect(M, y, UW, 6.5);
        fill(34, 197, 94); rect(M, y, 2, 6.5);
        font('bold', 6.5); color(134, 239, 172); txt(lbl, M + 5, y + 4.5);
        font('normal', 6.5); color(203, 213, 225);
        const valLines = pdf.splitTextToSize(val, UW - 30);
        txt(valLines[0], M + 26, y + 4.5);
        y += 6.5;
      }
      y += 2;

      // Procedimientos Específicos
      checkPage(14, `ERG ${guide.number}`);
      fill(22, 33, 51); rect(M, y, UW, 7);
      fill(99, 102, 241); rect(M, y, 3, 7);
      font('bold', 8); color(165, 180, 252); txt('PROCEDIMIENTOS ESPECIFICOS', M + 6, y + 5);
      y += 7;

      // Derrame / Fuga
      checkPage(8, `ERG ${guide.number}`);
      fill(20, 20, 35); rect(M, y, UW, 6);
      fill(59, 130, 246); rect(M, y, 3, 6);
      font('bold', 7); color(147, 197, 253); txt('DERRAME / FUGA', M + 6, y + 4.5);
      y += 6;
      for (const step of guide.emergencyResponse.spillLeak) {
        const lines = pdf.splitTextToSize(step, UW - 8);
        checkPage(lines.length * 5.5 + 1, `ERG ${guide.number}`);
        fill(17, 24, 39); rect(M, y, UW, lines.length * 5.5 + 1);
        font('normal', 6); color(203, 213, 225);
        for (let i = 0; i < lines.length; i++) txt(lines[i], M + 4, y + 4 + i * 5.5);
        y += lines.length * 5.5 + 1;
      }
      y += 2;

      // Intoxicación / Exposición
      checkPage(8, `ERG ${guide.number}`);
      fill(15, 28, 20); rect(M, y, UW, 6);
      fill(34, 197, 94); rect(M, y, 3, 6);
      font('bold', 7); color(134, 239, 172); txt('INTOXICACION / EXPOSICION', M + 6, y + 4.5);
      y += 6;
      for (const step of guide.firstAid) {
        const lines = pdf.splitTextToSize(step, UW - 8);
        checkPage(lines.length * 5.5 + 1, `ERG ${guide.number}`);
        fill(15, 27, 20); rect(M, y, UW, lines.length * 5.5 + 1);
        font('normal', 6); color(203, 213, 225);
        for (let i = 0; i < lines.length; i++) txt(lines[i], M + 4, y + 4 + i * 5.5);
        y += lines.length * 5.5 + 1;
      }
      y += 2;

      // Reacción Química
      checkPage(8, `ERG ${guide.number}`);
      fill(30, 20, 10); rect(M, y, UW, 6);
      fill(245, 158, 11); rect(M, y, 3, 6);
      font('bold', 7); color(253, 230, 138); txt('REACCION QUIMICA', M + 6, y + 4.5);
      y += 6;
      const rxSteps = [
        `1. Evacuar inmediatamente. Distancia minima: ${stripAccents(guide.publicSafety.isolationLargeSpill)}.`,
        '2. No intentar separar manualmente los productos que reaccionan.',
        `3. Notificar al ECU-911 (911) indicando guia ERG ${guide.number} y los productos involucrados.`,
        '4. Facilitar hojas de seguridad (SDS) a los servicios de emergencia.',
        '5. No reingresar hasta autorizacion de Bomberos o ECU-911.',
      ];
      for (const step of rxSteps) {
        const lines = pdf.splitTextToSize(step, UW - 8);
        checkPage(lines.length * 5.5 + 1, `ERG ${guide.number}`);
        fill(25, 20, 10); rect(M, y, UW, lines.length * 5.5 + 1);
        font('normal', 6); color(203, 213, 225);
        for (let i = 0; i < lines.length; i++) txt(lines[i], M + 4, y + 4 + i * 5.5);
        y += lines.length * 5.5 + 1;
      }
      y += 6;
    }
  }

  // SECTION 5 — Emergency numbers
  section('5.', 'NUMEROS DE EMERGENCIA ECUADOR', 239, 68, 68);

  // 911 — ancho completo, destacado
  checkPage(24, 'Numeros de Emergencia');
  const mainNum = EMERGENCY_NUMBERS[0];
  const [mnr, mng, mnb] = hexToRgb(mainNum.color);
  fill(35, 15, 15); rrect(M, y, UW, 20, 2);
  fill(mnr, mng, mnb); rrect(M, y, UW, 2, 1);
  font('bold', 22); color(mnr, mng, mnb); txt(mainNum.number, M + UW / 2, y + 14, { align: 'center' });
  font('normal', 7.5); color(148, 163, 184); txt(mainNum.name, M + UW / 2, y + 19, { align: 'center' });
  y += 24;

  // Restantes en fila
  const remNums = EMERGENCY_NUMBERS.slice(1);
  const remW = (UW - (remNums.length - 1) * 4) / remNums.length;
  checkPage(18, 'Numeros de Emergencia');
  for (let j = 0; j < remNums.length; j++) {
    const num = remNums[j];
    const nx = M + j * (remW + 4);
    const [nr, ng, nb] = hexToRgb(num.color);
    fill(22, 33, 51); rrect(nx, y, remW, 16, 2);
    fill(nr, ng, nb); rrect(nx, y, remW, 2, 1);
    font('bold', 14); color(nr, ng, nb); txt(num.number, nx + remW / 2, y + 10.5, { align: 'center' });
    font('normal', 6.5); color(148, 163, 184); txt(num.name, nx + remW / 2, y + 15, { align: 'center' });
  }
  y += 20;
  y += 4;

  // SECTION 6 — Evacuación y Firma
  section('6.', 'EVACUACION Y FIRMA', 168, 85, 247);

  const evacRoutes = [
    'RUTA PRINCIPAL: Dirigirse hacia la salida de emergencia mas cercana siguiendo la senalizacion verde.',
    'PUNTO DE ENCUENTRO: Ubicarse en el exterior del edificio, al menos 20 metros de la estructura, en zona despejada.',
    'ORDEN DE EVACUACION: Personal de zona afectada primero; luego zonas adyacentes; finalmente areas administrativas.',
    'PERSONAS CON MOVILIDAD REDUCIDA: El responsable de piso coordinara asistencia hacia la via de evacuacion accesible.',
    'PROHIBIDO: Usar ascensores durante la evacuacion. No regresar al interior hasta autorizacion de Bomberos o ECU-911.',
    'ROLL-CALL: El responsable de seguridad verificara la lista de personal en el punto de encuentro.',
    'COMUNICACION: El coordinador de emergencia notificara a los servicios de emergencia y a la gerencia de la empresa.',
  ];

  for (const route of evacRoutes) {
    checkPage(10, 'Evacuacion');
    const lines = pdf.splitTextToSize(route, UW - 10);
    fill(17, 24, 39); rect(M, y, UW, lines.length * 6.5 + 2);
    fill(168, 85, 247); rect(M, y, 3.5, lines.length * 6.5 + 2);
    font('normal', 7); color(203, 213, 225);
    for (let li = 0; li < lines.length; li++) {
      txt(lines[li], M + 7, y + 5 + li * 6.5);
    }
    y += lines.length * 6.5 + 4;
  }
  y += 4;

  y += 8;
  checkPage(130, 'Firma y Responsable');

  checkPage(16, 'Firma');
  fill(22, 33, 51); rect(M, y, UW, 8);
  fill(59, 130, 246); rect(M, y, 3.5, 8);
  font('bold', 8); color(147, 197, 253); txt('Firma y Responsable de Seguridad', M + 7, y + 5.5);
  y += 12;
  fill(22, 33, 51); rrect(M, y, UW, 80, 3);
  fill(59, 130, 246); rect(M, y, 4, 80);

  font('bold', 9); color(226, 232, 240); txt('Datos de Validacion del Plan', M + 10, y + 12);

  const dataRows2 = [
    ['Bodega:',   warehouse.name],
    ['Empresa:',  companyName],
    ['Emitido:',  genDate],
    ['Alcance:',  'Almacenamiento de Sustancias Quimicas Peligrosas'],
    ['Version:',  '1.0 — Generado por StorSafe'],
  ];
  dataRows2.forEach(([lbl, val], i) => {
    font('normal', 7.5); color(100, 116, 139); txt(lbl, M + 10, y + 22 + i * 8);
    font('normal', 7.5); color(203, 213, 225); txt(truncate(val, 100), M + 40, y + 22 + i * 8);
  });

  // Signature lines
  stroke(71, 85, 105); pdf.setDrawColor(71, 85, 105);
  line(M + 10, y + 67, M + 80, y + 67);
  font('normal', 7); color(71, 85, 105); txt('Responsable de Seguridad Quimica', M + 10, y + 72);

  line(pw - M - 70, y + 67, pw - M - 2, y + 67);
  txt('Fecha de revision', pw - M - 70, y + 72);

  y += 88;

  // StorSafe seal
  checkPage(30, 'Firma');
  fill(30, 15, 15); rrect(pw / 2 - 60, y, 120, 26, 3);
  fill(239, 68, 68); rrect(pw / 2 - 60, y, 120, 1.5, 1);
  font('bold', 10); color(239, 68, 68); txt('StorSafe', pw / 2, y + 11, { align: 'center' });
  font('normal', 7); color(100, 116, 139);
  txt('Plan de Emergencia Quimica generado automaticamente', pw / 2, y + 17, { align: 'center' });
  txt('Revisar y validar con el responsable de seguridad antes de publicar', pw / 2, y + 22.5, { align: 'center' });

  addFooter();

  const safeName = warehouse.name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
  pdf.save(`StorSafe_PlanEmergencia_${safeName}_${hoyEc()}.pdf`);
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function Emergency() {
  const { company } = useCompany();
  const { impersonated } = useImpersonation();
  const effectiveCompany = impersonated ?? company;

  const [warehouses,    setWarehouses]    = useState<Warehouse[]>([]);
  const [selectedWhId, setSelectedWhId]  = useState<string>('');
  const [zones,         setZones]         = useState<Zone[]>([]);
  const [allProducts,   setAllProducts]   = useState<Product[]>([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState('');
  const [generating,    setGenerating]    = useState(false);
  const [logoImg,       setLogoImg]       = useState<HTMLImageElement | null>(null);
  const [quickType, setQuickType] = useState<'incendio' | 'derrame' | 'exposicion' | 'reaccion' | 'persona' | null>(null);
  const [quickProductId, setQuickProductId] = useState<string>('');
  const [quickSearch, setQuickSearch] = useState('');
  // "Dónde está" del producto elegido (solo is_dev hasta que JC la libere).
  const conUbicaciones = ubicacionesHabilitadas(effectiveCompany);
  const [ubicQuick, setUbicQuick] = useState<UbicacionDescrita[] | null>(null);
  useEffect(() => {
    if (!conUbicaciones || !quickProductId || quickProductId === 'GENERAL') { setUbicQuick(null); return; }
    let vigente = true;
    fetchUbicacionesDeProductos([quickProductId])
      .then(m => { if (vigente) setUbicQuick(m.get(quickProductId) ?? []); })
      .catch(() => { if (vigente) setUbicQuick(null); });
    return () => { vigente = false; };
  }, [conUbicaciones, quickProductId]);

  useEffect(() => {
    const img = new Image();
    img.onload = () => setLogoImg(img);
    img.src = logoSrc;
  }, []);

  const productMap = new Map(allProducts.map(p => [p.id, p]));
  const selectedWh = warehouses.find(w => w.id === selectedWhId) ?? null;
  const whZones    = zones.filter(z => z.warehouse_id === selectedWhId);

  const whProductIds  = new Set(whZones.flatMap(z => z.product_ids));
  const whProducts    = Array.from(whProductIds).map(id => productMap.get(id)).filter(Boolean) as Product[];
  const classesPresent = new Set(whProducts.flatMap(p => p.sga_classes));

  const allIncompat: { a: Product; b: Product; zone: string }[] = [];
  const allSegr:     { a: Product; b: Product; zone: string }[] = [];
  for (const zone of whZones) {
    const { incomp, segr } = getZonePairs(zone, productMap);
    incomp.forEach(p => allIncompat.push({ ...p, zone: zone.name }));
    segr.forEach(p =>   allSegr.push({ ...p, zone: zone.name }));
  }

  const ergGuides = getUniqueErgGuides(whProducts.map(p => p.un_number));

  const riskLevel: 'high' | 'medium' | 'low' =
    allIncompat.length > 0 ? 'high' :
    allSegr.length > 0 ? 'medium' : 'low';

  const quickProduct = quickProductId && quickProductId !== 'GENERAL' ? productMap.get(quickProductId) ?? null : null;
  const quickGuide = quickProduct ? getErgGuide(quickProduct.un_number) : null;
  const quickFilteredProducts = whProducts.filter(p => {
    const q = quickSearch.trim().toLowerCase();
    if (!q) return true;
    return p.name.toLowerCase().includes(q) || (p.cas_number ?? '').includes(q) || (p.un_number ?? '').includes(q);
  });

  function resetQuick() {
    setQuickType(null);
    setQuickProductId('');
    setQuickSearch('');
  }

  const fetchAll = useCallback(async () => {
    if (!effectiveCompany) return;
    setLoading(true);
    setError('');
    const cid = effectiveCompany.id;
    const [
      { data: whs,   error: err1 },
      { data: zs,    error: err2 },
      { data: prods, error: err3 },
    ] = await Promise.all([
      supabase.from('warehouses').select('id,name,description').eq('company_id', cid).order('name'),
      // warehouse_zones no tiene company_id: se filtra por la empresa de su bodega.
      supabase.from('warehouse_zones').select('id,warehouse_id,name,product_ids,warehouses!inner(company_id)').eq('warehouses.company_id', cid),
      supabase.from('products').select('id,name,cas_number,un_number,sga_classes').eq('company_id', cid).order('name'),
    ]);
    if (err1 || err2 || err3) {
      setError('Error al cargar los datos. Por favor recarga la página.');
      setLoading(false);
      return;
    }
    setWarehouses(whs ?? []);
    setZones(zs ?? []);
    setAllProducts(prods ?? []);
    if (whs && whs.length > 0) setSelectedWhId(prev => prev || whs[0].id);
    setLoading(false);
  }, [effectiveCompany]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleGenerate = async () => {
    if (!selectedWh || !effectiveCompany) return;
    setGenerating(true);
    try {
      generateEmergencyPdf({
        warehouse: selectedWh,
        zones: whZones,
        productMap,
        allProducts,
        companyName: effectiveCompany.name,
        logoImg,
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="emg-root">
      {/* ── Controls ── */}
      <div className="emg-controls">
        <div className="emg-controls-left">
          <div className="emg-field-group">
            <label className="emg-label">Bodega</label>
            {loading ? (
              <div className="emg-select-skeleton" />
            ) : (
              <SelectBuscable
                className="emg-buscable"
                inputStyle={EMG_INPUT}
                ariaLabel="Bodega"
                value={selectedWhId}
                onChange={setSelectedWhId}
                disabled={warehouses.length === 0}
                obligatorio
                vacio={warehouses.length === 0 ? 'Sin bodegas registradas' : 'Elige una bodega'}
                opciones={warehouses.map(w => ({ value: w.id, label: w.name }))}
              />
            )}
          </div>

          {selectedWh && !loading && (
            <div className={`emg-risk-badge ${riskLevel}`}>
              <AlertTriIcon />
              {riskLevel === 'high'   ? 'Riesgo Alto'
               : riskLevel === 'medium' ? 'Riesgo Medio'
               : 'Riesgo Bajo'}
            </div>
          )}
        </div>

        <button
          className="emg-btn-download"
          onClick={handleGenerate}
          disabled={generating || loading || !selectedWh}
        >
          {generating
            ? <><span className="emg-spinner" /> Generando…</>
            : <><DownloadIcon /> Descargar PDF</>}
        </button>
      </div>

      {/* ── Preview ── */}
      <div className="emg-preview">
        {error ? (
          <div className="emg-empty">
            <AlertTriIcon />
            <p>{error}</p>
            <button className="emg-btn-download" onClick={fetchAll}>Reintentar</button>
          </div>
        ) : loading ? (
          <div className="emg-loading">
            <span className="emg-spinner large" />
            <p>Cargando datos…</p>
          </div>
        ) : !selectedWh ? (
          <div className="emg-empty">
            <WarehouseIcon />
            <p>Selecciona una bodega para ver el plan de emergencia</p>
          </div>
        ) : (
          <>
            {/* Cover card */}
            <div className="emg-cover-card">
              <div className="emg-cover-icon"><FireIcon /></div>
              <div className="emg-cover-body">
                <div className="emg-cover-title">PLAN DE EMERGENCIA QUÍMICA</div>
                <div className="emg-cover-sub">NTE INEN 2266:2013 · {effectiveCompany?.name}</div>
                <div className="emg-cover-wh">{selectedWh.name}</div>
              </div>
              <div className={`emg-risk-pill ${riskLevel}`}>
                {riskLevel === 'high' ? 'RIESGO ALTO' : riskLevel === 'medium' ? 'PRECAUCIÓN' : 'CONFORME'}
              </div>
            </div>

            {/* ¿Qué emergencia está ocurriendo? */}
            <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: 20, marginBottom: 20 }}>
              <h3 style={{ margin: '0 0 14px' }}>¿Qué emergencia está ocurriendo?</h3>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
                {([
                  { type: 'incendio' as const, label: 'Incendio', Icon: FireIcon, color: '#ef4444' },
                  { type: 'derrame' as const, label: 'Derrame / Fuga', Icon: SpillIcon, color: '#3b82f6' },
                  { type: 'exposicion' as const, label: 'Exposición', Icon: ExposureIcon, color: '#f97316' },
                  { type: 'reaccion' as const, label: 'Reacción Química', Icon: ReactionIcon, color: '#a855f7' },
                  { type: 'persona' as const, label: 'Persona Afectada', Icon: PersonIcon, color: '#22c55e' },
                ]).map(({ type, label, Icon, color }) => (
                  <button
                    key={type}
                    onClick={() => { setQuickType(type); setQuickProductId(''); setQuickSearch(''); }}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                      padding: '14px 8px', borderRadius: 10, cursor: 'pointer',
                      background: quickType === type ? color + '22' : '#0f172a',
                      border: `1.5px solid ${quickType === type ? color : '#334155'}`,
                      color: quickType === type ? color : '#cbd5e1',
                    }}
                  >
                    <Icon />
                    <span style={{ fontSize: 12, fontWeight: 700, textAlign: 'center' }}>{label}</span>
                  </button>
                ))}
              </div>

              {quickType && !quickProductId && (
                <div style={{ marginTop: 16, borderTop: '1px solid #334155', paddingTop: 16 }}>
                  <label style={{ fontSize: 13, fontWeight: 600 }}>Selecciona el producto involucrado</label>
                  <input
                    type="text"
                    placeholder="Buscar por nombre, CAS o UN"
                    value={quickSearch}
                    onChange={e => setQuickSearch(e.target.value)}
                    style={{ width: '100%', marginTop: 6, background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', padding: '8px 12px' }}
                  />
                  <div style={{ marginTop: 10, maxHeight: 220, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {quickFilteredProducts.map(p => (
                      <button
                        key={p.id}
                        onClick={() => setQuickProductId(p.id)}
                        style={{ textAlign: 'left', background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: '8px 12px', color: '#e2e8f0', cursor: 'pointer' }}
                      >
                        <strong>{p.name}</strong>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>
                          {p.un_number ? `UN${p.un_number}` : 'Sin UN'} {p.cas_number ? `· CAS ${p.cas_number}` : ''}
                        </div>
                      </button>
                    ))}
                    {quickFilteredProducts.length === 0 && <p style={{ color: '#64748b', fontSize: 13 }}>Sin resultados.</p>}
                  </div>
                  <button
                    onClick={() => setQuickProductId('GENERAL')}
                    style={{ marginTop: 10, width: '100%', background: 'transparent', border: '1px dashed #475569', borderRadius: 8, padding: '10px', color: '#94a3b8', cursor: 'pointer' }}
                  >
                    No sé qué producto es → Respuesta general de la bodega
                  </button>
                </div>
              )}

              {quickType && quickProductId && (
                <div style={{ marginTop: 16, borderTop: '1px solid #334155', paddingTop: 16 }}>
                  <button onClick={resetQuick} style={{ background: 'transparent', border: 'none', color: '#60a5fa', cursor: 'pointer', fontSize: 12, marginBottom: 10 }}>
                    ← Cambiar tipo de emergencia o producto
                  </button>

                  {quickProductId === 'GENERAL' && (
                    <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: 14 }}>
                      <p style={{ color: '#fbbf24', fontWeight: 700, margin: '0 0 8px' }}>Producto no identificado — respuesta general</p>
                      <p style={{ color: '#cbd5e1', fontSize: 13 }}>
                        Evacúa el área, notifica al ECU-911 (911) indicando bodega, tipo de emergencia y productos presentes conocidos, y no reingreses hasta autorización de Bomberos. Proporciona las hojas de seguridad (SDS) disponibles a los servicios de emergencia.
                      </p>
                    </div>
                  )}

                  {conUbicaciones && quickProduct && ubicQuick && (
                    <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: 12, marginBottom: 10 }}>
                      <p style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 13, margin: '0 0 6px' }}>Dónde está</p>
                      <UbicacionChips
                        ubicaciones={ubicQuick.filter(u => whZones.some(z => z.id === u.zonaId))}
                        vacio="Sin ubicación registrada en esta bodega"
                      />
                    </div>
                  )}

                  {quickProduct && !quickGuide && (
                    <p style={{ color: '#94a3b8', fontSize: 13 }}>
                      No se encontró guía ERG asociada a este producto (verifica que tenga número UN asignado). Consulta su hoja de seguridad (SDS).
                    </p>
                  )}

                  {quickProduct && quickGuide && (
                    <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: 14 }}>
                      <p style={{ color: '#60a5fa', fontSize: 12, margin: '0 0 8px' }}>
                        {quickProduct.name} — Guía ERG {quickGuide.number}
                      </p>

                      {quickType === 'incendio' && (
                        <>
                          <div style={{ color: '#cbd5e1', fontSize: 13, marginBottom: 6 }}><strong>Incendio pequeño:</strong> {quickGuide.fireByType.small}</div>
                          <div style={{ color: '#cbd5e1', fontSize: 13, marginBottom: 6 }}><strong>Incendio grande:</strong> {quickGuide.fireByType.large}</div>
                          <div style={{ color: '#cbd5e1', fontSize: 13 }}><strong>Tanque/cisterna:</strong> {quickGuide.fireByType.tank}</div>
                        </>
                      )}

                      {quickType === 'derrame' && quickGuide.emergencyResponse.spillLeak.map((step, i) => (
                        <div key={i} style={{ color: '#cbd5e1', fontSize: 13, marginBottom: 4 }}>{step}</div>
                      ))}

                      {(quickType === 'exposicion' || quickType === 'persona') && quickGuide.firstAid.map((step, i) => (
                        <div key={i} style={{ color: '#cbd5e1', fontSize: 13, marginBottom: 4 }}>{step}</div>
                      ))}

                      {quickType === 'reaccion' && (
                        <>
                          <div style={{ color: '#cbd5e1', fontSize: 13, marginBottom: 4 }}>1. Evacuar inmediatamente el área. Distancia mínima: {quickGuide.publicSafety.isolationLargeSpill}.</div>
                          <div style={{ color: '#cbd5e1', fontSize: 13, marginBottom: 4 }}>2. No intentar separar manualmente los productos que reaccionan.</div>
                          <div style={{ color: '#cbd5e1', fontSize: 13, marginBottom: 4 }}>3. Notificar al ECU-911 (911) indicando guía ERG {quickGuide.number} y los productos involucrados.</div>
                          <div style={{ color: '#cbd5e1', fontSize: 13, marginBottom: 4 }}>4. Proporcionar hojas de seguridad (SDS) a los servicios de emergencia.</div>
                          <div style={{ color: '#cbd5e1', fontSize: 13 }}>5. No reingresar hasta autorización de Bomberos o ECU-911.</div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Stats row */}
            <div className="emg-stats-row">
              <div className="emg-stat blue">
                <div className="emg-stat-val">{whZones.length}</div>
                <div className="emg-stat-lbl">Zonas</div>
              </div>
              <div className="emg-stat yellow">
                <div className="emg-stat-val">{whProducts.length}</div>
                <div className="emg-stat-lbl">Productos</div>
              </div>
              <div className="emg-stat red">
                <div className="emg-stat-val">{allIncompat.length}</div>
                <div className="emg-stat-lbl">Incompatibles</div>
              </div>
              <div className="emg-stat orange">
                <div className="emg-stat-val">{allSegr.length}</div>
                <div className="emg-stat-lbl">A Segregar</div>
              </div>
              <div className="emg-stat green">
                <div className="emg-stat-val">{ergGuides.length}</div>
                <div className="emg-stat-lbl">Guías ERG</div>
              </div>
            </div>

            {/* Sección 1 — Información de la Bodega */}
            <div className="emg-section">
              <div className="emg-section-hdr">
                <InfoIcon />
                <h3>Información de la Bodega</h3>
              </div>
              <div className="emg-wh-info-rows">
                {selectedWh.description && (
                  <div className="emg-wh-info-row">
                    <span className="emg-wh-info-label">Descripción</span>
                    <span className="emg-wh-info-value">{selectedWh.description}</span>
                  </div>
                )}
                <div className="emg-wh-info-row">
                  <span className="emg-wh-info-label">Empresa</span>
                  <span className="emg-wh-info-value">{effectiveCompany?.name}</span>
                </div>
              </div>
              {whZones.length > 0 && (
                <div className="emg-zone-list">
                  {whZones.map(z => {
                    const risk = getZoneRisk(z, productMap);
                    return (
                      <div key={z.id} className={`emg-zone-item ${risk}`}>
                        <span className="emg-zone-dot" />
                        <span className="emg-zone-name">{z.name}</span>
                        <span className="emg-zone-count">{z.product_ids.length} prod.</span>
                        <span className="emg-zone-risk">{risk === 'incompatible' ? 'ALERTA' : risk === 'segregar' ? 'PRECAUCIÓN' : 'OK'}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              {classesPresent.size > 0 && (
                <div className="emg-class-chips">
                  {SGA_CLASSES.filter(c => classesPresent.has(c.id)).map(c => (
                    <div key={c.id} className="emg-class-chip" style={{ borderColor: c.color + '60', background: c.bg }}>
                      <span className="emg-class-dot" style={{ background: c.color }} />
                      <span style={{ color: c.color }}>{c.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Sección 2 — Inventario de Sustancias Peligrosas */}
            <div className="emg-section">
              <div className="emg-section-hdr">
                <GridIcon />
                <h3>Inventario de Sustancias Peligrosas ({whProducts.length})</h3>
              </div>
              {whProducts.length === 0 ? (
                <p className="emg-empty-small">No hay productos asignados a zonas en esta bodega.</p>
              ) : (
                <div className="emg-inventory-list">
                  {whProducts.map((p, i) => (
                    <div key={p.id} className="emg-inventory-row">
                      <span className="emg-inv-num">{i + 1}</span>
                      <span className="emg-inv-name">{p.name}</span>
                      <div className="emg-inv-tags">
                        {p.un_number && <span className="emg-inv-tag un">UN{p.un_number}</span>}
                        {p.un_number && !getErgGuide(p.un_number) && (
                          <span
                            className="emg-inv-tag"
                            style={{ backgroundColor: '#fef3c7', color: '#92400e', fontSize: '0.65rem' }}
                            title="Guía ERG no disponible en esta versión para este número UN. Consulta la Guía ERG 2024 oficial en www.phmsa.dot.gov"
                          >
                            ERG no disponible
                          </span>
                        )}
                        {p.cas_number && <span className="emg-inv-tag cas">{p.cas_number}</span>}
                      </div>
                      <div className="emg-inv-classes">
                        {p.sga_classes.map(cls => {
                          const sgaCls = SGA_CLASSES.find(c => c.id === cls);
                          return sgaCls ? (
                            <span key={cls} className="emg-inv-class-dot" style={{ background: sgaCls.color }} title={sgaCls.label} />
                          ) : null;
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Sección 3 — Riesgos Identificados por Zona */}
            {(allIncompat.length > 0 || allSegr.length > 0) && (
              <div className="emg-section">
                <div className="emg-section-hdr">
                  <AlertTriIcon />
                  <h3>Riesgos Identificados por Zona</h3>
                </div>
                {allIncompat.length > 0 && (
                  <div className="emg-risk-block red">
                    <div className="emg-risk-block-title">
                      Incompatibles <span className="emg-badge red">{allIncompat.length}</span>
                    </div>
                    {allIncompat.slice(0, 5).map((p, i) => (
                      <div key={i} className="emg-pair-row red">
                        <span>{p.a.name}</span>
                        <span className="emg-pair-sep">vs</span>
                        <span>{p.b.name}</span>
                        <span className="emg-pair-zone">{p.zone}</span>
                      </div>
                    ))}
                    {allIncompat.length > 5 && <div className="emg-more">+{allIncompat.length - 5} más en el PDF</div>}
                  </div>
                )}
                {allSegr.length > 0 && (
                  <div className="emg-risk-block yellow">
                    <div className="emg-risk-block-title">
                      A Segregar <span className="emg-badge yellow">{allSegr.length}</span>
                    </div>
                    {allSegr.slice(0, 4).map((p, i) => (
                      <div key={i} className="emg-pair-row yellow">
                        <span>{p.a.name}</span>
                        <span className="emg-pair-sep">vs</span>
                        <span>{p.b.name}</span>
                        <span className="emg-pair-zone">{p.zone}</span>
                      </div>
                    ))}
                    {allSegr.length > 4 && <div className="emg-more">+{allSegr.length - 4} más en el PDF</div>}
                  </div>
                )}
              </div>
            )}

            {/* Sección 4 — Guías ERG 2024 con Procedimientos */}
            <div className="emg-section">
              <div className="emg-section-hdr">
                <ErgIcon />
                <h3>Guías ERG 2024{ergGuides.length > 0 ? ` (${ergGuides.length})` : ''}</h3>
              </div>
              {ergGuides.length === 0 ? (
                <p className="emg-empty-small">
                  Ningún producto de esta bodega tiene número UN/NA registrado. Agrega el N° UN/NA en la ficha de cada producto para ver las guías ERG correspondientes.
                </p>
              ) : (
                <div className="emg-erg-list">
                  {ergGuides.map((guide) => {
                    const linked = whProducts.filter(p => getErgGuideNumber(p.un_number) === guide.number);
                    return (
                      <div key={guide.number} className="emg-erg-card">
                        <div className="emg-erg-header">
                          <span className="emg-erg-badge">ERG {guide.number}</span>
                          <span className="emg-erg-name">{guide.name}</span>
                        </div>
                        <div className="emg-erg-products">
                          {linked.map(p => (
                            <span key={p.id} className="emg-erg-product-chip">
                              {p.name}{p.un_number ? ` · UN${p.un_number}` : ''}
                            </span>
                          ))}
                        </div>
                        <div className="emg-erg-body">
                          <div className="emg-erg-block red">
                            <div className="emg-erg-block-title">Incendio / Explosión</div>
                            <p>{guide.hazards.fireExplosion}</p>
                          </div>
                          <div className="emg-erg-block yellow">
                            <div className="emg-erg-block-title">Salud</div>
                            <p>{guide.hazards.health}</p>
                          </div>
                          <div className="emg-erg-block blue">
                            <div className="emg-erg-block-title">Distancias de Aislamiento</div>
                            <div className="emg-erg-distances">
                              <span><strong>Derrame peq.:</strong> {guide.publicSafety.isolationSmallSpill}</span>
                              <span><strong>Derrame grande:</strong> {guide.publicSafety.isolationLargeSpill}</span>
                              <span><strong>Incendio:</strong> {guide.publicSafety.fireIsolation}</span>
                            </div>
                          </div>
                        </div>
                        <div className="emg-erg-fire-ppe">
                          <div className="emg-erg-fire-block">
                            <div className="emg-erg-block-title">Respuesta — Incendio por Tipo</div>
                            <div className="emg-erg-fire-rows">
                              <div className="emg-erg-fire-row">
                                <span className="emg-erg-fire-tag small">Pequeño</span>
                                <span>{guide.fireByType.small}</span>
                              </div>
                              <div className="emg-erg-fire-row">
                                <span className="emg-erg-fire-tag large">Grande</span>
                                <span>{guide.fireByType.large}</span>
                              </div>
                              <div className="emg-erg-fire-row">
                                <span className="emg-erg-fire-tag tank">Tanques</span>
                                <span>{guide.fireByType.tank}</span>
                              </div>
                            </div>
                          </div>
                          <div className="emg-erg-ppe-block">
                            <div className="emg-erg-block-title">EPP del Respondedor</div>
                            <div className="emg-erg-ppe-rows">
                              <div className="emg-erg-ppe-row"><span className="emg-erg-ppe-icon"><SuitIcon /></span><span>{guide.ppe.suit}</span></div>
                              <div className="emg-erg-ppe-row"><span className="emg-erg-ppe-icon"><RespiratorIcon /></span><span>{guide.ppe.respirator}</span></div>
                              <div className="emg-erg-ppe-row"><span className="emg-erg-ppe-icon"><GlovesIcon /></span><span>{guide.ppe.gloves}</span></div>
                              <div className="emg-erg-ppe-row"><span className="emg-erg-ppe-icon"><BootsIcon /></span><span>{guide.ppe.boots}</span></div>
                            </div>
                          </div>
                        </div>
                        <div className="emg-erg-procedures">
                          <div className="emg-erg-proc-title">Procedimientos Específicos</div>
                          <div className="emg-erg-proc-items">
                            <div className="emg-erg-proc-item emg-erg-proc-blue">
                              <div className="emg-erg-proc-header">DERRAME / FUGA</div>
                              {guide.emergencyResponse.spillLeak.map((step, si) => (
                                <div key={si} className="emg-erg-proc-step">{step}</div>
                              ))}
                            </div>
                            <div className="emg-erg-proc-item emg-erg-proc-green">
                              <div className="emg-erg-proc-header">INTOXICACIÓN / EXPOSICIÓN</div>
                              {guide.firstAid.map((step, si) => (
                                <div key={si} className="emg-erg-proc-step">{step}</div>
                              ))}
                            </div>
                            <div className="emg-erg-proc-item emg-erg-proc-orange">
                              <div className="emg-erg-proc-header">REACCIÓN QUÍMICA</div>
                              <div className="emg-erg-proc-step">1. Evacuar inmediatamente el área. Distancia mínima: {guide.publicSafety.isolationLargeSpill}.</div>
                              <div className="emg-erg-proc-step">2. No intentar separar manualmente los productos que reaccionan.</div>
                              <div className="emg-erg-proc-step">3. Notificar al ECU-911 (911) indicando guía ERG {guide.number} y los productos involucrados.</div>
                              <div className="emg-erg-proc-step">4. Proporcionar hojas de seguridad (SDS) a los servicios de emergencia.</div>
                              <div className="emg-erg-proc-step">5. No reingresar hasta autorización de Bomberos o ECU-911.</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Sección 5 — Números de Emergencia Ecuador */}
            <div className="emg-section">
              <div className="emg-section-hdr">
                <PhoneIcon />
                <h3>Números de Emergencia Ecuador</h3>
              </div>
              <div className="emg-numbers-grid">
                {EMERGENCY_NUMBERS.map((n, i) => (
                  <div key={i} className={`emg-number-card${n.isMain ? ' emg-number-main' : ''}`} style={{ borderTopColor: n.color }}>
                    <div className="emg-number-val" style={{ color: n.color }}>{n.number}</div>
                    <div className="emg-number-name">{n.name}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sección 6 — Evacuación y Firma */}
            <div className="emg-section">
              <div className="emg-section-hdr">
                <EvacIcon />
                <h3>Evacuación y Firma</h3>
              </div>
              <div className="emg-evac-list">
                {[
                  'RUTA PRINCIPAL: Dirigirse a la salida de emergencia más cercana siguiendo la señalización verde.',
                  'PUNTO DE ENCUENTRO: Ubicarse en el exterior, al menos 20 m de la estructura, en zona despejada.',
                  'ORDEN DE EVACUACIÓN: Personal de zona afectada primero; luego zonas adyacentes; finalmente áreas administrativas.',
                  'PERSONAS CON MOVILIDAD REDUCIDA: El responsable de piso coordinará asistencia hacia la vía de evacuación accesible.',
                  'PROHIBIDO: Usar ascensores durante la evacuación. No reingresar hasta autorización de Bomberos o ECU-911.',
                  'ROLL-CALL: El responsable de seguridad verificará la lista de personal en el punto de encuentro.',
                  'COMUNICACIÓN: El coordinador de emergencia notificará a los servicios de emergencia y a la gerencia.',
                ].map((step, i) => (
                  <div key={i} className="emg-evac-item">
                    <span className="emg-evac-dot" />
                    <span>{step}</span>
                  </div>
                ))}
              </div>
              <div className="emg-sig-notice">
                El plan debe ser firmado por el Responsable de Seguridad Química. Ver campo de firma en el PDF generado.
              </div>
            </div>

            {/* PDF notice */}
            <div className="emg-pdf-notice">
              <FireIcon />
              <div>
                <div className="emg-pdf-title">El PDF incluye el plan completo</div>
                <div className="emg-pdf-sub">Portada · Información de bodega · Inventario · Riesgos por zona · Guías ERG 2024 con procedimientos · Números de emergencia · Evacuación · Firma</div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function FireIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M8.5 14.5A2.5 2.5 0 0011 17c.55 0 1.05-.22 1.41-.59"/>
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" strokeOpacity="0"/>
      <path d="M17.66 11.2C17.43 10.9 17.15 10.64 16.89 10.38C16.22 9.78 15.46 9.35 14.82 8.72C13.33 7.26 13 4.85 13.95 3C13 3.23 12.17 3.75 11.46 4.32C8.87 6.4 7.85 10.07 9.07 13.22C9.11 13.32 9.15 13.42 9.15 13.55C9.15 13.77 9 13.97 8.8 14.05C8.57 14.15 8.33 14.09 8.14 13.93C8.08 13.88 8.04 13.83 8 13.76C6.87 12.33 6.69 10.28 7.45 8.64C5.78 10 4.87 12.3 5 14.47C5.06 14.97 5.12 15.47 5.29 15.97C5.43 16.57 5.7 17.17 6 17.7C7.08 19.43 8.95 20.67 10.96 20.92C13.1 21.19 15.39 20.8 17.03 19.32C18.86 17.66 19.5 15 18.56 12.72L18.43 12.46C18.22 12.04 17.66 11.2 17.66 11.2Z"/>
    </svg>
  );
}

function SuitIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 3h6l1 3-2 2v11a2 2 0 0 1-2 2h-0a2 2 0 0 1-2-2V8L8 6z" />
      <path d="M9 3 5 6l2 4M15 3l4 3-2 4" />
    </svg>
  );
}

function RespiratorIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3a5 5 0 0 0-5 5v3a5 5 0 0 0 10 0V8a5 5 0 0 0-5-5z" />
      <circle cx="9.5" cy="10" r="1.3" />
      <circle cx="14.5" cy="10" r="1.3" />
      <path d="M9 15h6M8 19h8l-1 2H9z" />
    </svg>
  );
}

function GlovesIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 11V6a1.5 1.5 0 0 1 3 0v4M10 10V5a1.5 1.5 0 0 1 3 0v5M13 10V6a1.5 1.5 0 0 1 3 0v5" />
      <path d="M16 11V8a1.5 1.5 0 0 1 3 0v6a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5v-3a2 2 0 0 1 4 0" />
    </svg>
  );
}

function BootsIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 3v9l-4 3v3a1 1 0 0 0 1 1h6a2 2 0 0 0 2-2v-2h4a2 2 0 0 0 2-2c0-1-1-2-3-2h-3V3z" />
      <path d="M8 6h6" />
    </svg>
  );
}

function SpillIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3c3 4 6 7.5 6 11a6 6 0 0 1-12 0c0-3.5 3-7 6-11z" />
    </svg>
  );
}

function ExposureIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4.5 3.5-7 8-7s8 2.5 8 7" />
      <path d="M12 8l6-3M12 8l-6-3" />
    </svg>
  );
}

function ReactionIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 2v6L4 19a2 2 0 0 0 2 3h12a2 2 0 0 0 2-3l-5-11V2" />
      <path d="M7 2h10M8 15h8" />
    </svg>
  );
}

function PersonIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="7" r="3.5" />
      <path d="M5 21c0-4 3-6.5 7-6.5s7 2.5 7 6.5" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  );
}

function AlertTriIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  );
}

function WarehouseIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
  );
}


function GridIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/>
      <line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/>
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.01 1.18 2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
    </svg>
  );
}

function ErgIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/>
      <path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/>
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  );
}

function EvacIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10 3H6a2 2 0 00-2 2v14a2 2 0 002 2h4"/>
      <polyline points="16 17 21 12 16 7"/>
      <line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  );
}
